import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

function ActionBar({ saving, onSave, onCancel }) {
  return (
    <Paper
      elevation={5}
      onClick={(event) => event.stopPropagation()}
      sx={{
        position: "absolute",
        right: 0,
        top: -42,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        p: 0.35,
        borderRadius: 999,
        border: "1px solid rgba(25,36,47,.10)",
      }}
    >
      <Tooltip title="Guardar cambio">
        <span>
          <IconButton
            size="small"
            onClick={onSave}
            disabled={saving}
            sx={{ color: "#16834b" }}
          >
            {saving ? <CircularProgress size={17} /> : <CheckRoundedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cancelar">
        <span>
          <IconButton size="small" onClick={onCancel} disabled={saving} sx={{ color: "#9d2d24" }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

export function EditableText({
  value,
  fallback,
  onSave,
  disabled = false,
  multiline = false,
  minRows = 1,
  component = "p",
  sx,
  inputSx,
  ariaLabel = "Editar texto",
}) {
  const displayed = String(value || "").trim() || fallback || "Texto sin configurar";
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value || fallback || ""));
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!editing) setDraft(String(value || fallback || ""));
  }, [value, editing]);

  const begin = (event) => {
    event?.stopPropagation();
    if (disabled) return;
    setDraft(String(value || fallback || ""));
    setEditing(true);
  };

  const cancel = (event) => {
    event?.stopPropagation();
    if (saving) return;
    setDraft(String(value || fallback || ""));
    setEditing(false);
  };

  const save = async (event) => {
    event?.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (_) {
      // El editor queda abierto para reintentar.
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancel(event);
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      save(event);
    }
    if (!multiline && event.key === "Enter") {
      event.preventDefault();
      save(event);
    }
  };

  if (editing) {
    return (
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ position: "relative", display: "block", width: "100%", minWidth: 90 }}
      >
        <ActionBar saving={saving} onSave={save} onCancel={cancel} />
        <InputBase
          inputRef={inputRef}
          autoFocus
          fullWidth
          multiline={multiline}
          minRows={multiline ? minRows : undefined}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
          sx={{
            bgcolor: "rgba(255,255,255,.98)",
            color: "#19242F",
            border: "2px solid #F35A3A",
            borderRadius: 1.5,
            px: 1,
            py: multiline ? 0.75 : 0.3,
            boxShadow: "0 8px 24px rgba(25,36,47,.18)",
            font: "inherit",
            lineHeight: "inherit",
            ...inputSx,
          }}
        />
      </Box>
    );
  }

  return (
    <Typography
      component={component}
      onClick={begin}
      title="Hacé clic para editar"
      sx={{
        position: "relative",
        display: "inline-block",
        maxWidth: "100%",
        borderRadius: 1,
        outline: "1px dashed transparent",
        outlineOffset: 4,
        cursor: disabled ? "default" : "text",
        transition: "outline-color .15s ease, background-color .15s ease",
        "&:hover": disabled
          ? undefined
          : {
              outlineColor: "rgba(243,90,58,.78)",
              bgcolor: "rgba(243,90,58,.07)",
              "& .inline-edit-icon": { opacity: 1 },
            },
        ...sx,
      }}
    >
      {displayed}
      {!disabled ? (
        <EditRoundedIcon
          className="inline-edit-icon"
          sx={{
            ml: 0.65,
            fontSize: "0.82em",
            opacity: 0,
            verticalAlign: "middle",
            transition: "opacity .15s ease",
          }}
        />
      ) : null}
    </Typography>
  );
}

export function EditableButton({
  value,
  fallback,
  onSave,
  disabled = false,
  variant = "contained",
  sx,
}) {
  const displayed = String(value || "").trim() || fallback || "Botón";
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value || fallback || ""));
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setDraft(String(value || fallback || ""));
  }, [value, editing]);

  const save = async (event) => {
    event.stopPropagation();
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (_) {
      // El editor queda abierto para reintentar.
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box onClick={(event) => event.stopPropagation()} sx={{ position: "relative", minWidth: 150 }}>
        <ActionBar
          saving={saving}
          onSave={save}
          onCancel={(event) => {
            event.stopPropagation();
            if (!saving) setEditing(false);
          }}
        />
        <InputBase
          autoFocus
          fullWidth
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setEditing(false);
            if (event.key === "Enter") save(event);
          }}
          sx={{
            bgcolor: "#fff",
            color: "#19242F",
            border: "2px solid #F35A3A",
            borderRadius: 999,
            px: 1.5,
            py: 0.45,
            boxShadow: "0 8px 24px rgba(25,36,47,.18)",
            fontWeight: 900,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) {
            setDraft(String(value || fallback || ""));
            setEditing(true);
          }
        }}
        sx={{
          appearance: "none",
          border: variant === "outlined" ? "1px solid currentColor" : 0,
          cursor: disabled ? "default" : "text",
          font: "inherit",
          ...sx,
        }}
        title="Hacé clic para editar el texto del botón"
      >
        {displayed}
      </Box>
    </Box>
  );
}
